import employeesApi from 'Api/axios/employees-api';
import { courseApprovalsApi } from 'Api/axios/course-approvals-api';
import { S3_EVIDENCES } from 'Constants/General';
import useFetchData from 'Hooks/useFetchData';
import useModal from 'Hooks/useModal';
import useTranslation from 'Hooks/useTranslation';
import { useCallback, useEffect, useState } from 'react';
import { s3 } from 'Services/AWS/s3';
import { toast } from 'sonner';
import type { DevelopmentPlanEntryI } from 'types';

interface Props {
  userId: string;
}

interface ApproveUserCoursesParams {
  userCourseIds: string[];
  file: File | null;
  evaluationDate?: Date; // si se pasa, el frontend la envía; si undefined, backend asigna hoy
  courseId: string;
  instructorId?: string | null; // instructor externo (catálogo); si null, el backend lo liga a "Kappa"
  instructorUserId?: string | null; // instructor interno (empleado); excluyente con instructorId
}

const useEmployeeDevelopmentPlan = ({ userId }: Props) => {
  const { t2 } = useTranslation('EmployeeAssess');
  const [selectedCourse, setSelectedCourse] = useState<DevelopmentPlanEntryI | null>(null);
  const { open: openApprove, toggle: toggleApprove } = useModal();
  const { open: openResetProgress, toggle: toggleResetProgress } = useModal();
  const { open: openCourseAssign, toggle: toggleCourseAssigner } = useModal();
  const { open: openDeassignConfirmation, toggle: toggleDeassignConfirmation } = useModal();

  const { data, fetchOne } = useFetchData({
    getOne: id => employeesApi.readDevelopmentPlan(id),
  });

  useEffect(() => {
    fetchOne(userId);
  }, [userId]);

  const uploadEvaluationFileS3 = async (key: string, file: Blob, contentType: string): Promise<boolean> => {
    if (!file) return false;
    try {
      const ok = await s3.uploadFileToBucketWithKeyProvided(key, file as unknown as Buffer, contentType);
      return !!ok;
    } catch (caught) {
      console.error('Error subiendo evidencia a S3:', caught);
      return false;
    }
  };

  /**
   * Aprueba uno o varios userCourses con evidencia compartida y/o fecha retroactiva.
   * Sube la evidencia a S3 y aprueba por REST contra el recurso del curso.
   */
  const approveUserCourses = useCallback(
    async ({
      userCourseIds,
      file,
      evaluationDate,
      courseId,
      instructorId,
      instructorUserId,
    }: ApproveUserCoursesParams): Promise<boolean> => {
      let key: string | undefined;

      if (file) {
        key = `${S3_EVIDENCES}/${userId}/${courseId}/${file.name}`;
        const uploadOk = await uploadEvaluationFileS3(key, file, file.type);
        if (!uploadOk) {
          toast.error(t2('upload_evidence_error') as string);
          return false;
        }
      }

      const dedupedIds = Array.from(new Set(userCourseIds));

      const response = await courseApprovalsApi.approve(courseId, {
        userCourseIds: dedupedIds,
        evidenceKey: key,
        evaluationDate: evaluationDate ? evaluationDate.toISOString() : undefined,
        instructorId: instructorId ?? undefined,
        instructorUserId: instructorUserId ?? undefined,
      });

      if (response.error) {
        // La evidencia ya está en S3 y la aprobación no ocurrió: sin este
        // borrado quedaría un archivo huérfano por cada intento fallido.
        if (key) await s3.deleteObject(key);
        toast.error(response.error);
        return false;
      }

      toast.success(t2('approve_success', [String(response.data?.approved ?? dedupedIds.length)]) as string);
      fetchOne(userId);
      return true;
    },
    [userId, fetchOne, t2]
  );

  /**
   * Reinicia el progreso de un curso. La desasignación vive en
   * `unassignSelectedCourse`, que habla con el recurso REST del empleado.
   */
  const updateUserCourse = async (action: 'reset_progress'): Promise<boolean> => {
    if (!selectedCourse) return false;

    if (action !== 'reset_progress') {
      console.warn(
        `updateUserCourse llamado con acción no soportada: "${action}". Use approveUserCourses para aprobar.`
      );
      return false;
    }

    const response = await employeesApi.resetUserCourseProgress(userId, selectedCourse.userCourseId);
    if (response.error) {
      toast.error(response.error);
      return false;
    }
    toggleResetProgress(false);
    toast.success(t2('reset_progress_success') as string);

    fetchOne(userId);
    return true;
  };

  /** `POST /employees/:id/courses` */
  const assignCourses = useCallback(
    async (courseIds: string[]): Promise<boolean> => {
      const response = await employeesApi.assignCourses(userId, courseIds);

      if (response.error) {
        toast.error(response.error);
        return false;
      }

      toast.success(t2('assign_success') as string);
      fetchOne(userId);
      return true;
    },
    [userId, fetchOne, t2]
  );

  /** `DELETE /employees/:id/courses/:courseId` */
  const unassignSelectedCourse = useCallback(async (): Promise<boolean> => {
    if (!selectedCourse) return false;

    const response = await employeesApi.unassignCourse(userId, selectedCourse.Course.id);

    if (response.error) {
      toast.error(response.error);
      return false;
    }

    toggleDeassignConfirmation(false);
    toast.success(t2('deassign_success') as string);
    fetchOne(userId);
    return true;
  }, [userId, selectedCourse, fetchOne, toggleDeassignConfirmation, t2]);

  return {
    selectedCourse,
    setSelectedCourse,
    openApprove,
    toggleApprove,
    openResetProgress,
    toggleResetProgress,
    developmentPlan: data,
    approveUserCourses,
    updateUserCourse,
    openCourseAssign,
    toggleCourseAssigner,
    assignCourses,
    unassignSelectedCourse,
    openDeassignConfirmation,
    toggleDeassignConfirmation,
  };
};

export default useEmployeeDevelopmentPlan;
